import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, forwardRef, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';

@Component({
  selector: 'app-input-field',
  standalone: true,
  imports: [CommonModule],
  template: `
  <div class="mb-2 relative">
    <label [for]="id" class="block text-text absolute left-4 -top-1 transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-focus:-top-1 peer-focus:text-sm peer-focus:text-white text-xs peer-text-white bg-bg z-1 px-1" *ngIf="label"> {{ label }}<span class="text-red-500" *ngIf="ngControl?.control?.errors?.['required'] || required">*</span> </label>
      <input
        [type]="type"
        [id]="id"
        [name]="name"
        [placeholder]="placeholder"
        [value]="value"
        [min]="min"
        [max]="max"
        [step]="step"
        [disabled]="disabled"
        [ngClass]="inputClasses"
        (input)="onInput($event)"
        (blur)="onTouched()"
      />

     <p *ngIf="invalid && submit" class="text-danger text-base sm:text-sm mt-1  mb-3">
    {{ getFirstErrorMessage() }}
  </p>
    </div>
  `,
  host: {
    // tabindex: '0'   // Make component focusable
  }

})
export class InputFieldComponent implements ControlValueAccessor {
  @Input() type: string = 'text';
  @Input() id?: string = '';
  @Input() name?: string = '';
  @Input() placeholder?: string = '';
  @Input() value: string | number = '';
  @Input() min?: any = 0;
  @Input() max?: any;
  @Input() step?: any;
  @Input() disabled: boolean = false;
  @Input() success: boolean = false;
  @Input() error: boolean = false;
  @Input() hint?: string;
  @Input() className: string = '';
  @Input() label?: string = '';
  @Input() submit?: boolean = false;
  @Input() required?: boolean = false;

  @Output() valueChange = new EventEmitter<string | number>();

  onChange: any = () => { };
  onTouched: any = () => { };


  constructor(@Optional() @Self() public ngControl: NgControl) {

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this; // 🔹 required for form binding
    }
  }

  get inputClasses(): string {
    let inputClasses = `h-[3rem] w-full rounded-lg border border-text appearance-none px-3 py-2.5 text-sm text-text shadow-theme-xs placeholder:text-text focus:outline-hidden focus:border-white ${this.className}`;

    if (this.disabled) {
      inputClasses += ` text-text border-text opacity-40 bg-gray-100 cursor-not-allowed`;
    } else if (this.error && this.submit) {
      inputClasses += ` !border-danger focus:border-danger`;
    } else if (this.success && this.submit) {
      inputClasses += ` border-success-500 focus:border-success-300`;
    }
    else if (this.invalid && this.submit) {
      inputClasses += ` !border-danger focus:border-danger`;
    }
    else if (!this.invalid && this.touched && this.submit) {
      inputClasses += ` bg-transparent text-text border-text focus:border-brand-300`;
    }
    else {
      inputClasses += ` bg-transparent text-text border-text focus:border-brand-300`;
    }
    return inputClasses;
  }

  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const newValue = this.type === 'number' ? +input.value : input.value;
    this.value = newValue;
    this.valueChange.emit(newValue);

    this.onChange(newValue);
  }
  get control() {
    return this.ngControl?.control;
  }
  get invalid(): boolean {
    return !!this.control?.invalid && (this.control?.touched || this.control?.dirty);
  }
  get touched(): boolean {
    return !!this.control?.touched;
  }
  writeValue(value: any): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  getFirstErrorMessage(): string {
    if (!this.control?.errors) return '';
    if (this.control.errors['required']) return 'This field is required';
     if (this.control.errors['min'] && this.type == 'number') {
      return `Minimum number is ${this.control.errors['min'].min}`;
    }
    if (this.control.errors['max'] && this.type == 'number') {
      return `Maximum number is ${this.control.errors['max'].max}`;
    }
    if (this.control.errors['minlength'])
      return `Minimum ${this.control.errors['minlength'].requiredLength} characters`;
    if (this.control.errors['maxlength'])
      return `Maximum ${this.control.errors['maxlength'].requiredLength} characters`;
    if (this.control.errors['min'])
      return `Minimum ${this.control.errors['min'].min} characters`;
    if (this.control.errors['max'])
      return `Maximum ${this.control.errors['max'].max} characters`;
    if (this.control.errors['pattern'])
      return `Invalid format`;

    return 'Invalid value';
  }

}
